import { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Download, Image as ImageIcon } from 'lucide-react';
import { BANNERS, NAVY } from './banners/config';
import { drawBanner } from './banners/canvas';
import { BannerPreview } from './banners/BannerPreview';

export const BannerGenerator = ({ user, publicUrl }) => {
    const qrRefs = useRef({});
    const [downloading, setDownloading] = useState(null);
    const [previewIdx, setPreviewIdx] = useState(0);

    const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Agent';
    const agency = user?.agency_name || '';
    const city = user?.city || '';
    const tagline = 'Scannez pour voir mon planning';

    const buildSourceUrl = (sourceId) => {
        try {
            const u = new URL(publicUrl);
            u.searchParams.set('utm_source', sourceId);
            return u.toString();
        } catch {
            return `${publicUrl}?utm_source=${sourceId}`;
        }
    };

    const generate = async (banner) => {
        const svg = qrRefs.current[banner.id]?.querySelector('svg');
        if (!svg) {
            toast.error('QR code non disponible');
            return;
        }
        setDownloading(banner.id);
        try {
            const serializer = new XMLSerializer();
            const svgString = serializer.serializeToString(svg);
            const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const svgUrl = URL.createObjectURL(svgBlob);

            const qrImg = new Image();
            qrImg.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
                qrImg.onload = resolve;
                qrImg.onerror = reject;
                qrImg.src = svgUrl;
            });

            const canvas = document.createElement('canvas');
            canvas.width = banner.width;
            canvas.height = banner.height;
            const ctx = canvas.getContext('2d');

            drawBanner({
                ctx,
                w: banner.width,
                h: banner.height,
                layout: banner.layout,
                fullName,
                agency,
                city,
                qrImg,
                tagline,
            });

            URL.revokeObjectURL(svgUrl);

            const filename = `scalimmo_${banner.id}_${fullName.replace(/\s/g, '_')}.png`;
            canvas.toBlob((blob) => {
                if (!blob) {
                    toast.error('Erreur lors de la génération');
                    return;
                }
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success(`${banner.label} téléchargé`);
            }, 'image/png');
        } catch (e) {
            console.error(e);
            toast.error('Erreur lors de la génération de la bannière');
        } finally {
            setDownloading(null);
        }
    };

    const previewBanner = BANNERS[previewIdx];

    return (
        <div className="space-y-5" data-testid="banner-generator">
            {/* Hidden QR codes (one per banner with proper UTM source) for canvas conversion */}
            <div className="hidden">
                {BANNERS.map((b) => (
                    <div key={b.id} ref={(el) => { qrRefs.current[b.id] = el; }}>
                        <QRCodeSVG value={buildSourceUrl(b.id)} size={400} level="M" fgColor={NAVY} bgColor="#ffffff" includeMargin={false} />
                    </div>
                ))}
            </div>

            <div className="bg-scalimmo-gold/10 border border-scalimmo-gold/30 rounded-xl p-4">
                <p className="text-sm text-scalimmo-navy flex items-start gap-2">
                    <ImageIcon className="w-4 h-4 mt-0.5 shrink-0 text-scalimmo-gold" />
                    <span>
                        Téléchargez et imprimez ces bannières sur vos panneaux <strong>"À vendre"</strong>, vos flyers ou partagez-les en story.
                        Chaque scan ouvre votre profil et incrémente vos vues.
                    </span>
                </p>
            </div>

            {/* Format selector */}
            <div className="grid grid-cols-3 gap-2">
                {BANNERS.map((b, i) => (
                    <button
                        key={b.id}
                        onClick={() => setPreviewIdx(i)}
                        className={`text-left p-3 rounded-lg border transition-all ${
                            previewIdx === i
                                ? 'border-scalimmo-navy bg-scalimmo-navy/5'
                                : 'border-border/60 hover:border-scalimmo-navy/40'
                        }`}
                        data-testid={`banner-tab-${b.id}`}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-scalimmo-navy">{b.icon}</span>
                            <span className="font-medium text-sm text-scalimmo-navy">{b.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{b.sub}</p>
                    </button>
                ))}
            </div>

            {/* Live preview */}
            <div className="bg-muted/30 rounded-xl p-6 flex items-center justify-center">
                <BannerPreview
                    banner={previewBanner}
                    fullName={fullName}
                    agency={agency}
                    city={city}
                    publicUrl={buildSourceUrl(previewBanner.id)}
                    tagline={tagline}
                />
            </div>

            <Button
                onClick={() => generate(previewBanner)}
                className="w-full btn-primary gap-2"
                disabled={downloading === previewBanner.id}
                data-testid={`download-banner-${previewBanner.id}`}
            >
                <Download className="w-4 h-4" />
                {downloading === previewBanner.id ? 'Génération...' : `Télécharger — ${previewBanner.label}`}
                <Badge className="bg-white/10 text-white border-white/20 ml-1 text-[10px]">
                    PNG HD
                </Badge>
            </Button>
        </div>
    );
};
